// proceduralLibrary/designs/DesignPanel.jsx
//
// SVG wallpaper Designs library (DL-063c). A grid of 12 generated designs, each
// appearing once with its default palette; selecting one applies it to the
// wallpaper (cover) via onApply -> setBgGradient (currentBg === 3), and reveals
// the ThemeStrip to recolor the selection live.

import React, { useMemo, useState, useCallback } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { DESIGNS, generateDesignSvg, PALETTE_BY_NAME } from "./designGenerators";
import { buildDesignCss, parseDesignCss } from "../toWallpaperCss";
import ThemeStrip from "../ThemeStrip";
import { colors } from "../../../utils/theme";
import { fontFamily } from "../../../theme/tokens";

const svgToDataUri = (svg) =>
  `data:image/svg+xml,${svg
    .replace(/"/g, "'")
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/&/g, "%26")}`;

const DesignPanel = ({ value, onApply, segmentColor, wheelColor }) => {
  const saved = useMemo(() => parseDesignCss(value), [value]);
  const [selectedId, setSelectedId] = useState(saved?.id || null);
  const [paletteName, setPaletteName] = useState(saved?.paletteName || null);
  // Which match pill (if any) produced the CURRENT recolor — see ThemeStrip.
  // Not restorable from a reopened `saved.palette` (we bake the resulting
  // triple, not which pill produced it), so a reopened custom palette starts
  // with no pill claimed active — honest, since neither Segment nor Wheel can
  // be asserted from the hex alone.
  const [matchSource, setMatchSource] = useState(null);

  // Thumbnails: each design at its default palette (recolor is applied on the
  // live surface, not the thumbnail, so the grid stays a stable index).
  const thumbById = useMemo(() => {
    const acc = {};
    DESIGNS.forEach((d) => {
      acc[d.id] = svgToDataUri(generateDesignSvg(d.id, PALETTE_BY_NAME[d.palette]));
    });
    return acc;
  }, []);

  // Accepts either a curated palette NAME (string) or an already-derived
  // {light,deep,accent} triple (a Segment/Wheel match-pill recolor) —
  // buildDesignCss forwards either straight into generateDesignSvg, which
  // bakes the actual applied palette into the marker (see toWallpaperCss.js).
  const apply = useCallback(
    (id, paletteNameOrTriple) => {
      const css = buildDesignCss(id, paletteNameOrTriple);
      if (css && onApply) onApply(css);
    },
    [onApply]
  );

  const handleSelect = (d) => {
    setSelectedId(d.id);
    setMatchSource(null);
    const pal = paletteName || d.palette;
    setPaletteName(pal);
    apply(d.id, pal);
  };

  const handleTheme = (name, triple, source) => {
    if (name) {
      setPaletteName(name);
      setMatchSource(null);
      apply(selectedId, name);
    } else if (triple && selectedId) {
      // Derived (Segment/Wheel match-pill) recolor: clear the now-stale named
      // palette so ThemeStrip stops highlighting a chip that no longer
      // reflects the applied colors, and record which pill matched so the
      // strip can truthfully mark IT active instead.
      setPaletteName(null);
      setMatchSource(source || "match");
      apply(selectedId, triple);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography
        sx={{ fontWeight: "bold", fontSize: "14px", pl: 1, mb: 1, fontFamily }}
      >
        Wallpaper designs
      </Typography>

      <Grid container spacing={1} sx={{ mb: 1 }}>
        {DESIGNS.map((d) => {
          const selected = selectedId === d.id;
          return (
            <Grid item key={d.id}>
              <Box
                role="button"
                tabIndex={0}
                aria-label={d.label}
                aria-pressed={selected}
                title={d.label}
                onClick={() => handleSelect(d)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(d);
                  }
                }}
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "8px",
                  overflow: "hidden",
                  cursor: "pointer",
                  border: selected ? `2px solid ${colors.PrimaryPurple}` : "1px solid #e1e4e8",
                  // rgba derived from colors.PrimaryPurple #9B5AF8 at 35% —
                  // no exact token for this alpha step yet, kept literal.
                  // #e1e4e8 (unselected border) and #C9A8FF (hover) also have
                  // no matching design token yet — kept literal.
                  boxShadow: selected ? "0 0 0 2px rgba(155,90,248,.35)" : "none",
                  "&:hover": { borderColor: "#C9A8FF" },
                  "&:focus-visible": { outline: "none", boxShadow: `0 0 0 2px ${colors.lightPurple}` },
                }}
              >
                <img
                  src={thumbById[d.id]}
                  alt={`${d.label} design`}
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {selectedId && (
        <ThemeStrip
          label="Color theme"
          selectedName={paletteName}
          selectedMatch={matchSource}
          onSelect={handleTheme}
          segmentColor={segmentColor}
          wheelColor={wheelColor}
        />
      )}
    </Box>
  );
};

export default DesignPanel;
